import { Block, Flex, ModeSwitcher, Table, Tbody, Td, Th, Thead, Tr, Typography } from '@aloft/toolkit-sui';

import React, { useState } from 'react';

import { BulkCompResult } from './bulkAnalysisTypes.ts';

type FilterMode = 'all' | 'mismatches' | 'matches';

const FILTER_ITEMS: { id: FilterMode; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'mismatches', label: 'Mismatches' },
  { id: 'matches', label: 'Matches' },
];

type Props = {
  results: BulkCompResult[];
  onSelect: (result: BulkCompResult) => void;
};

export const BulkResultsTable = ({ results, onSelect }: Props) => {
  const [filter, setFilter] = useState<FilterMode>('all');

  const completed = results.filter((r) => r.status !== 'pending');

  const filtered = completed.filter((r) => {
    if (filter === 'all') return true;
    if (r.status === 'error') return filter === 'mismatches';

    const qualityMismatch = r.aiQuality != null && r.aiQuality !== r.comp.uadQuality;
    const conditionMismatch =
      r.aiCondition != null && r.comp.uadCondition != null && r.aiCondition !== r.comp.uadCondition;
    const isMismatch = qualityMismatch || conditionMismatch;

    return filter === 'mismatches' ? isMismatch : !isMismatch;
  });

  // Sort: errors first, then mismatches, then matches
  const sorted = [...filtered].sort((a, b) => {
    const rank = (r: BulkCompResult) => {
      if (r.status === 'error') return 0;
      const qMismatch = r.aiQuality != null && r.aiQuality !== r.comp.uadQuality;
      const cMismatch =
        r.aiCondition != null && r.comp.uadCondition != null && r.aiCondition !== r.comp.uadCondition;
      return qMismatch || cMismatch ? 1 : 2;
    };
    return rank(a) - rank(b);
  });

  if (completed.length === 0) return null;

  return (
    <Flex flexDirection="column" gap="12px">
      <Flex justifyContent="space-between" alignItems="center">
        <Typography variant="h3">Results ({completed.length})</Typography>
        <ModeSwitcher
          items={FILTER_ITEMS}
          value={filter}
          variant="secondary"
          size="sm"
          onChange={(v) => setFilter(v as FilterMode)}
        />
      </Flex>

      <Block overflowX="auto">
        <Table>
          <Thead>
            <Tr>
              <Th>Address</Th>
              <Th>Quality (Report)</Th>
              <Th>Quality (Gemini)</Th>
              <Th>Condition (Report)</Th>
              <Th>Condition (Gemini)</Th>
              <Th>ms</Th>
            </Tr>
          </Thead>
          <Tbody>
            {sorted.map((r) => {
              const qualityMatch =
                r.aiQuality != null ? r.aiQuality === r.comp.uadQuality : null;
              const conditionMatch =
                r.aiCondition != null && r.comp.uadCondition != null
                  ? r.aiCondition === r.comp.uadCondition
                  : null;
              const isClickable = r.status === 'success';
              const rowStyle: React.CSSProperties = isClickable ? { cursor: 'pointer' } : {};

              return (
                <tr
                  key={r.comp.compID}
                  onClick={isClickable ? () => onSelect(r) : undefined}
                  style={rowStyle}
                  onMouseEnter={
                    isClickable
                      ? (e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'var(--aloft-color-hover1, #f5f5f5)'; }
                      : undefined
                  }
                  onMouseLeave={
                    isClickable
                      ? (e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = ''; }
                      : undefined
                  }
                >
                  <Td>
                    <Typography variant="body2">
                      {r.comp.address ?? r.comp.compID.slice(0, 8)}
                    </Typography>
                    {r.status === 'error' && (
                      <Typography variant="body2" color="textError">
                        {r.errorMessage}
                      </Typography>
                    )}
                  </Td>
                  <Td>
                    <Typography variant="body2">{r.comp.uadQuality}</Typography>
                  </Td>
                  <Td>
                    <Typography
                      variant="body2"
                      color={
                        r.aiQuality == null ? 'textSecondary' : qualityMatch ? 'textSuccess' : 'textError'
                      }
                    >
                      {r.aiQuality ?? '—'}
                    </Typography>
                  </Td>
                  <Td>
                    <Typography variant="body2">{r.comp.uadCondition ?? '—'}</Typography>
                  </Td>
                  <Td>
                    <Typography
                      variant="body2"
                      color={
                        r.aiCondition == null ? 'textSecondary' : conditionMatch ? 'textSuccess' : 'textError'
                      }
                    >
                      {r.aiCondition ?? '—'}
                    </Typography>
                  </Td>
                  <Td>
                    <Typography variant="body2">{r.timingMs ?? '—'}</Typography>
                  </Td>
                </tr>
              );
            })}
          </Tbody>
        </Table>
      </Block>
    </Flex>
  );
};
